import {definitionSchema, dictionarySchema, musicSchema, productSchema, shopSchema} from 'apis'
import {baseModel, channelModel, userModel} from 'apis/model'
import {
    articleSchema,
    channelSchema,
    codiDocumentSchema,
    feedSchema,
    organizationSchema,
    postSchema,
    userSchema,
} from 'apis/schema'
import {keywordSchema} from 'apis/schema/keyword'
import {SelectEntityItem} from 'components/SelectEntityItem'
import {SelectEntityItemByIdname} from 'components/SelectEntityItemByIdname'
import {ROOT_URL} from 'envs/_current/config'
import _ from 'lodash'
import {LocalContext} from 'modules/local/LocalContext'
import useRouter from 'modules/navigation/useRouter'
import React from 'react'
import {Helmet} from 'react-helmet-async'
import {Route, Switch} from 'react-router-dom'
import {churchSchema, courseClassSchema, courseSchema, examinationSchema, missalSchema, profileSchema} from '../apis'
import {askSchema, pollSchema} from '../apis/schema'
import Translate from '../modules/local/Translate'
import {godwordsSchema} from "../apis/godwords";
import {getId} from "../apis/model/base";
import {getRouteSearchParams} from "../envs/_current/history";
import {staticPaths} from "../routes/staticPaths";
import {eventSchema} from "../apis/schema/event";

const aliasTitle = {
    home: 'Kristo Network - Living Faith, Every Day',
    news: 'Kristo Network - Living Faith, Every Day',
    notification: 'Notifications',
    category: 'category',
    discovery: 'Discovery',
    missals: 'missals',
    start: 'Kristo Network - Living Faith, Every Day',
    welcome: 'Kristo Network - Living Faith, Every Day',
}

const aliasDescription = {
    home: 'Up-to-date information about the Catholic Church, with numerous features to support the faith life, management and participation in Christian communities',
}

const aliasKeywords = {
    home: 'Kristo Network, Thiên Chúa, God, Dieu, Đức Giê-su, Jesus, Jésus, Saint Spirit, Chúa Thánh Thần, Kinh Thánh, Bible, Tân ước, Cựu ước, Old testament, New testament, Evangile, gospel, Phúc âm, Tin mừng, Bonne nouvelles, Công giáo, Catholic, catholique, Kitô giáo, Christianity, Christianisme, Chrétien, Kitô hữu, giáo lý công giáo, giáo luật công giáo, Thần học, triết học, Kitô học, thánh nhạc, thánh lễ, bài đọc, suy niệm, bài giảng, Lời Chúa, Kinh phụng vụ, thánh nhạc, thánh ca, Công đồng Vatican II, Đức giáo hoàng, Hồng Y, Giám mục, Linh mục, Tu sĩ, Mẹ Maria, Hội đồng giám mục, hội dòng, Dòng tu, Thiếu Nhi Thánh Thể, Dòng Tên, Dòng Đa Minh, Dòng Mến Thánh Giá, Dòng Chúa Cứu Thế, Dòng Phao lô, Ca Đoàn, Gia trưởng, Hiền mẫu, Lễ sinh, Giáo hội, Giáo phận, Giáo xứ, Caritas, Bác ái',
}

function setLanguageToUrl(url, lang) {
    let _url = ''
    try {
        _url = new URL(url)
    } catch (e) {
        try {
            _url = new URL(window.location.origin + url)
        } catch (e) {
        }
    }
    if (!!!_url) return _url

    let _search_params = _url.searchParams

    // new value of "id" is set to "101"
    _search_params.set('lang', lang)

    // change the search property of the main url
    _url.search = _search_params.toString()

    return _url.toString()
}

const toJSONLDSafe = (data) => {
    if (!data) return null;

    try {
        if (typeof data === "string") {
            JSON.parse(data); // validate JSON string
            return data;
        }

        return JSON.stringify(data);
    } catch (e) {
        console.error("Invalid JSON-LD:", e, data);
        return null;
    }
};

const renderMeta = ({
                        _type = '',
                        title = '',
                        description = '',
                        keywords = '',
                        url = '',
                        urls = [],
                        image = '',
                        language = 'vi',
                        type = 'article',
                        see_also = [],
                        structuredJSON,
                    }) => {
    const structuredJSONSafe = toJSONLDSafe(structuredJSON);

    return (
        <>
            <Translate>
                {(t) => (
                    <Helmet>
                        {/* <!-- Primary Meta Tags --> */}
                        <meta charSet="utf-8"/>
                        <title>{title ? t(title) : ''}</title>
                        <meta
                            httpEquiv="content-language"
                            content={language}
                        />
                        <link
                            ref="canonical"
                            href={url}
                        />
                        <meta
                            name="description"
                            content={t(description || '')}
                        />

                        <link
                            id="canonical"
                            rel="canonical"
                            href={url}
                        />
                        <meta
                            property="og:url"
                            content={url}
                        />
                        <meta
                            property="og:type"
                            content={type}
                        />
                        <meta
                            property="og:title"
                            content={title ? t(title) : ''}
                        />
                        <meta
                            property="og:description"
                            content={t(description || '')}
                        />
                        {_.map(see_also, function (e, key) {
                            return (
                                <meta
                                    key={key}
                                    property="og:see_also"
                                    content={ROOT_URL + '/t/' + e}
                                />
                            )
                        })}
                        {!!image && (
                            <meta
                                property="og:image"
                                content={image}
                            />
                        )}
                        {!!image && (
                            <meta
                                property="og:image:width"
                                content={600}
                            />
                        )}
                        {!!image && (
                            <meta
                                property="og:image:height"
                                content={314}
                            />
                        )}

                        <meta
                            property="og:locale"
                            content="vi_VN"
                        />

                        <link
                            href={url}
                            hrefLang="x-default"
                            rel="alternate"
                        />

                        {_type !== 'category' &&
                        ['en', 'vi'].map((e) => (
                            <link
                                key={e}
                                href={setLanguageToUrl(url, e)}
                                hrefLang={e}
                                rel="alternate"
                            />
                        ))}
                        {urls.map((e) => (
                            <link
                                key={e.id}
                                href={e.url}
                                hrefLang={e.id}
                                rel="alternate"
                            />
                        ))}

                        <meta
                            property="twitter:card"
                            content="summary_large_image"
                        />
                        <meta
                            property="twitter:url"
                            content={url}
                        />
                        <meta
                            property="twitter:title"
                            content={title ? t(title) : ''}
                        />
                        <meta
                            property="twitter:description"
                            content={t(description || '')}
                        />
                        <meta
                            property="twitter:image"
                            content={image}
                        />
                    </Helmet>
                )}
            </Translate>
            {!!structuredJSONSafe && (
                <script
                    key="jsonld"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{__html: structuredJSONSafe}}
                />
            )}
        </>
    )
}
const MetaData = renderMeta
const routes = [
    <Route
        key="/t/:id"
        path="/t/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={keywordSchema}>
                    {(item) => {
                        const schemaOrg = _.get(item, 'schema')
                        const structuredJSON = schemaOrg || undefined

                        return (
                            (item &&
                                renderMeta({
                                    url: ROOT_URL + '/t/' + item.idname,
                                    title: item.title,
                                    description: item.description,
                                    keywords: item.keyword_alt,
                                    structuredJSON,
                                })) ||
                            null
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/c/:cateId"
        path="/c/:cateId"
        render={(props) => {
            const id = _.get(props, 'match.params.cateId')
            return (
                <LocalContext.Consumer>
                    {({appConfig: item}) => {
                        if (!(item && item.categoryEntities && item.categoryEntities[id]))
                            return null

                        const schemaOrg = _.get(item.categoryEntities[id], 'schema')
                        const structuredJSON = schemaOrg || undefined
                        return renderMeta({
                            _type: 'category',
                            type: 'Category',
                            url: ROOT_URL + '/c/' + item.categoryEntities[id].idname,
                            title: item.categoryEntities[id].title,
                            description: item.categoryEntities[id].description,
                            keywords: item.categoryEntities[id].keywords,
                            structuredJSON,
                        })
                    }}
                </LocalContext.Consumer>
            )
        }}
    />,
    <Route
        key="/category/:cateId/:refCateId"
        path="/category/:cateId/:refCateId"
        render={(props) => {
            const id = _.get(props, 'match.params.cateId')
            const ref_id = _.get(props, 'match.params.refCateId')

            return (
                <LocalContext.Consumer>
                    {({appConfig: item}) => {
                        if (!(item && item.categoryEntities && item.categoryEntities[id]))
                            return null

                        const schemaOrg = _.get(item.categoryEntities[id], 'schema')
                        const structuredJSON = schemaOrg || undefined
                        return renderMeta({
                            _type: 'category',
                            type: 'Category',
                            url:
                                ROOT_URL +
                                '/category/' +
                                _.get(item.categoryEntities[id], 'idname') +
                                '/' +
                                _.get(item.categoryEntities[id], 'alt_idname', '#'),
                            urls: _.get(item.categoryEntities[id], 'urls', []),
                            title: item.categoryEntities[id].title,
                            description: item.categoryEntities[id].description,
                            keywords: Array.isArray(item.categoryEntities[id].keywords)
                                ? _.get(item.categoryEntities[id], 'keywords', []).reduce(
                                    (result, kw) => {
                                        result += kw.keyword + ', '
                                        return result
                                    },
                                    ''
                                )
                                : item.categoryEntities[id].keywords,
                            structuredJSON,
                        })
                    }}
                </LocalContext.Consumer>
            )
        }}
    />,
    <Route
        key="/news/article/:id"
        path="/news/article/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id');

            return (
                <SelectEntityItem
                    item={id}
                    schema={articleSchema}>
                    {(item) => {
                        const schemaOrg = _.get(item, 'schema')
                        const structuredJSON = schemaOrg || undefined

                        return (
                            (item &&
                                renderMeta({
                                    ...item,
                                    url: ROOT_URL + '/news/article/' + getId(item),
                                    image: _.get(item, 'overview.photo'),
                                    title: _.get(item, 'overview.title'),
                                    description: _.get(item, 'overview.description'),
                                    keywords: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result += kw.keyword + ', '
                                        return result
                                    }, ''),
                                    see_also: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result.push(kw.idname)
                                        return result
                                    }, []),
                                    structuredJSON,
                                })) ||
                            null
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/news/post/:id"
        path="/news/post/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={postSchema}>
                    {(item) => {
                        const schemaOrg = _.get(item, 'schema')
                        const structuredJSON = schemaOrg || undefined

                        return (
                            (item &&
                                renderMeta({
                                    ...item,
                                    structuredJSON,
                                    url: ROOT_URL + '/news/post/' + getId(item),
                                    image: _.get(item, 'overview.photo'),
                                    title: _.get(item, 'overview.title'),
                                    description: _.get(item, 'overview.description'),
                                    keywords: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result += kw.keyword + ', '
                                        return result
                                    }, ''),
                                    see_also: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result.push(kw.idname)
                                        return result
                                    }, []),
                                })) ||
                            null
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/news/ask/:id"
        path="/news/ask/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={askSchema}>
                    {(item) => {
                        const schemaOrg = _.get(item, 'schema')
                        const structuredJSON = schemaOrg || undefined

                        return (
                            (item &&
                                renderMeta({
                                    ...item,
                                    structuredJSON,
                                    url: ROOT_URL + '/news/ask/' + getId(item),
                                    image: _.get(item, 'overview.photo'),
                                    title: _.get(item, 'overview.title'),
                                    description: _.get(item, 'overview.description'),
                                    keywords: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result += kw.keyword + ', '
                                        return result
                                    }, ''),
                                    see_also: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result.push(kw.idname)
                                        return result
                                    }, []),
                                })) ||
                            null
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/news/poll_question/:id"
        path="/news/poll_question/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={pollSchema}>
                    {(item) => {
                        const schemaOrg = _.get(item, 'schema')
                        const structuredJSON = schemaOrg || undefined

                        return (
                            (item &&
                                renderMeta({
                                    ...item,
                                    structuredJSON,
                                    url: ROOT_URL + '/news/poll_question/' + getId(item),
                                    image: _.get(item, 'overview.photo'),
                                    title: _.get(item, 'overview.title'),
                                    description: _.get(item, 'overview.description'),
                                    keywords: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result += kw.keyword + ', '
                                        return result
                                    }, ''),
                                    see_also: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result.push(kw.idname)
                                        return result
                                    }, []),
                                })) ||
                            null
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/news/:type/:id"
        path="/news/:type/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={feedSchema}>
                    {(item) => {
                        return null
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/channel/:id"
        path="/channel/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={channelSchema}>
                    {(item) => {
                        const schemaOrg = _.get(item, 'schema')
                        const structuredJSON = schemaOrg || undefined

                        return (
                            (item || null) && (
                                <MetaData
                                    type="Channel"
                                    language={item.language}
                                    url={ROOT_URL + '/channel/' + item.idname}
                                    {...channelModel.getMeta(item)}
                                    keywords={item.keywords}
                                    structuredJSON={structuredJSON}
                                />
                            )
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/news"
        path="/news"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            url: ROOT_URL + staticPaths.news.pathname,
                            image: ROOT_URL + staticPaths.news.pathname,
                            keywords: t("channel page keywords"),
                            title: t('channels_heading'),
                            type: 'website',
                            description: t("channels_description"),
                        })
                    }
                </Translate>
            )
        }}
    />,

    <Route
        key="/examination/:id"
        path="/examination/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={examinationSchema}>
                    {(item) => {
                        const schemaOrg = _.get(item, 'schema')
                        const structuredJSON = schemaOrg || undefined
                        return (
                            (item || null) && (
                                <MetaData
                                    language={item.language}
                                    url={ROOT_URL + '/examination/' + item.idname}
                                    {...baseModel.getMeta(item)}
                                    keywords={item.keywords}
                                    structuredJSON={structuredJSON}
                                    type="Examination"
                                />
                            )
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/examination"
        path="/examination"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            title: t("examination_heading"),
                            type: 'website',
                            description: "examination_description",
                            image: ROOT_URL + staticPaths.examinations.pathname,
                            keywords: t('examination page keywords'),
                            url: ROOT_URL + window.location.pathname,
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/shop/:id"
        path="/shop/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={shopSchema}>
                    {(item) => {
                        const schemaOrg = _.get(item, 'schema')
                        const structuredJSON = schemaOrg || undefined

                        return (
                            (item || null) && (
                                <MetaData
                                    language={item.language}
                                    url={ROOT_URL + '/shop/' + item.idname}
                                    {...baseModel.getMeta(item)}
                                    keywords={item.keywords}
                                    structuredJSON={structuredJSON}
                                    type="Shop"
                                />
                            )
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/product/:id"
        path="/product/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={productSchema}>
                    {(item) => {
                        const schemaOrg = _.get(item, 'schema')
                        const structuredJSON = schemaOrg || undefined

                        return (
                            (item || null) && (
                                <MetaData
                                    language={item.language}
                                    url={ROOT_URL + '/product/' + item.idname}
                                    {...baseModel.getMeta(item)}
                                    keywords={item.keywords}
                                    structuredJSON={structuredJSON}
                                    type="Product"
                                />
                            )
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/book/:id"
        path="/book/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id');
            const search = _.get(props, 'location.search');
            return (
                <SelectEntityItem
                    item={id}
                    schema={codiDocumentSchema}>
                    {(item) => {
                        const schemaOrg = _.get(item, 'schema')
                        const overview = _.get(item, 'overview')
                        const structuredJSON = schemaOrg || undefined

                        return (
                            (item &&
                                renderMeta({
                                    ...overview,
                                    structuredJSON,
                                    url: ROOT_URL + '/book/' + item.idname + search,
                                    image: _.get(item, 'overview.photo'),
                                    title: _.get(item, 'overview.title'),
                                    description: _.get(item, 'overview.description'),
                                    keywords: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result += kw.keyword + ', '
                                        return result
                                    }, ''),
                                    see_also: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result.push(kw.idname)
                                        return result
                                    }, []),
                                    type: "Book"
                                })) ||
                            null
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/wog/:id"
        path="/wog/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id');
            return (
                <SelectEntityItem
                    item={id}
                    schema={godwordsSchema}>
                    {(item) => {
                        const schemaOrg = _.get(item, 'schema')
                        const structuredJSON = schemaOrg || undefined

                        return (
                            (item &&
                                renderMeta({
                                    ...item,
                                    structuredJSON,
                                    url: ROOT_URL + '/wog/' + getId(item),
                                    image: _.get(item, 'overview.photo'),
                                    title: _.get(item, 'overview.title'),
                                    description: _.get(item, 'overview.description'),
                                    keywords: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result += kw.keyword + ', '
                                        return result
                                    }, ''),
                                    see_also: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result.push(kw.id)
                                        return result
                                    }, []),
                                })) ||
                            null
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/course/:id"
        path="/course/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={courseSchema}>
                    {(item) => {
                        const schemaOrg = _.get(item, 'schema')
                        const overview = _.get(item, 'overview', {})
                        const structuredJSON = schemaOrg || undefined

                        return (
                            (item &&
                                renderMeta({
                                    ...overview,
                                    structuredJSON,
                                    url: ROOT_URL + '/course/' + item.idname,
                                    image: _.get(item, 'overview.photo'),
                                    title: _.get(item, 'overview.title'),
                                    description: _.get(item, 'overview.description'),
                                    keywords: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result += kw.keyword + ', '
                                        return result
                                    }, ''),
                                    see_also: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result.push(kw.idname)
                                        return result
                                    }, []),
                                    type: "Course"
                                })) ||
                            null
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/bibles"
        path="/bibles"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            url: ROOT_URL + staticPaths.bibles.pathname,
                            image: ROOT_URL + "/images/default/bible.webp",
                            keywords: t("bibles page keywords"),
                            title: t('bibles_heading'),
                            type: 'website',
                            description: t("bibles_description"),
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/prayers"
        path="/prayers"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            url: ROOT_URL + staticPaths.prayers.pathname,
                            image: ROOT_URL + "/images/default/loi-cau-nguyen-y-nguyen.webp",
                            title: t('prayers_title'),
                            type: 'website',
                            description: t("prayers_description"),
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key={staticPaths.prayerBooks.pathname}
        path={staticPaths.prayerBooks.pathname}
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            url: ROOT_URL + staticPaths.prayerBooks.pathname,
                            image: ROOT_URL + "/images/default/kinh-lay-cha.webp",
                            title: t('prayer_books_title'),
                            type: 'website',
                            description: t("prayer_books_description"),
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/churches"
        path="/churches"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            url: ROOT_URL + staticPaths.churches.pathname,
                            image: ROOT_URL + "/images/default/churchs-masstimes.webp",
                            keywords: t("catholic mass schedule keywords"),
                            title: t('churches_heading'),
                            type: 'website',
                            description: t("churches_description"),
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/church/:id"
        path="/church/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={churchSchema}>
                    {(item) => {
                        const schemaOrg = _.get(item, 'schema')
                        const overview = _.get(item, 'overview', {})
                        const structuredJSON = schemaOrg || undefined
                        return (
                            (item &&
                                renderMeta({
                                    ...overview,
                                    structuredJSON,
                                    image: _.get(item, 'overview.photo'),
                                    url: ROOT_URL + '/church/' + item?.idname,
                                    type: 'Church',
                                })) ||
                            null
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,

    <Route
        key="/pilgrimage"
        path="/pilgrimage"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: "Website",
                            url: ROOT_URL + staticPaths.pilgrimage.pathname,
                            image: ROOT_URL + "/images/banner_pilgrimage.jpg",
                            keywords: t("Catholic pilgrimage, journey of faith, prayer, Christian roots, Catholic travel"),
                            title: t('pilgrimage_heading'),
                            description: t("pilgrimage_description"),
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/missals"
        path="/missals"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: "Website",
                            url: ROOT_URL + '/missals',
                            image: ROOT_URL + "/images/default/roman-missal.webp",
                            keywords: t("missals page keywords"),
                            title: t("missals_heading"),
                            description: t("missals_description"),
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/missal/:id"
        path="/missal/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={missalSchema}>
                    {(item) => {
                        const schemaOrg = _.get(item, 'schema')
                        const structuredJSON = schemaOrg || undefined

                        return (
                            (item &&
                                renderMeta({
                                    ...item,
                                    structuredJSON,
                                    url: ROOT_URL + '/missal/' + item.idname,
                                    image: _.get(item, 'overview.photo'),
                                    title: _.get(item, 'overview.title'),
                                    description: _.get(item, 'overview.description'),
                                    keywords: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result += kw.keyword + ', '
                                        return result
                                    }, ''),
                                    see_also: _.get(item, 'keywords', []).reduce((result, kw) => {
                                        result.push(kw.idname)
                                        return result
                                    }, []),
                                    type: "Missal",
                                })) ||
                            null
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/user/:id"
        path="/user/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItemByIdname
                    item={id}
                    schema={userSchema}>
                    {(item) => {
                        return (
                            (item && (
                                <MetaData
                                    type={"User"}
                                    url={ROOT_URL + '/user/' + item.username}
                                    {...userModel.getMeta(item)}
                                />
                            )) ||
                            null
                        )
                    }}
                </SelectEntityItemByIdname>
            )
        }}
    />,
    <Route
        key="/organization/:id"
        path="/organization/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={organizationSchema}>
                    {(item) => {

                        const schemaOrg = _.get(item, 'schema')
                        const structuredJSON = schemaOrg || undefined

                        return (
                            (item &&
                                renderMeta({
                                    type: "Organization",
                                    url: ROOT_URL + '/organization/' + item.idname,
                                    title: _.get(item, 'overview.title'),
                                    description: _.get(item, 'overview.description'),
                                    keywords: _.get(item, 'overview.keywords'),
                                    photo: _.get(item, 'overview.photo'),
                                    structuredJSON,
                                })) ||
                            null
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/discovery"
        path="/discovery"
        render={(props) => {
            const params = getRouteSearchParams();
            const type = params.get("type");
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: "Search",
                            url: ROOT_URL + props?.location?.pathname + props?.location?.search + props?.location?.hash,
                            title: [t(type ?? ""), t(aliasTitle.discovery), t('search anythings in Kristo Network')].filter(e => !!e).join(" - "),
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/profile/:id"
        path="/profile/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={profileSchema}>
                    {(item) => {
                        const schemaOrg = _.get(item, 'schema')
                        const structuredJSON = schemaOrg || undefined

                        return (
                            (item || null) && (
                                <MetaData
                                    type={"Profile"}
                                    image={_.get(
                                        item,
                                        'photo',
                                        ROOT_URL + '/images/default/saints.webp'
                                    )}
                                    url={ROOT_URL + '/profile/' + item.idname}
                                    {...baseModel.getMeta(item)}
                                    structuredJSON={structuredJSON}
                                />
                            )
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/news"
        path="/news"
        exact={true}
        render={() =>
            renderMeta({
                title: aliasTitle['news'],
                type: 'website',
                description: aliasDescription['home'],
                keywords: aliasKeywords['home'],
                url: ROOT_URL + window.location.pathname,
            })
        }
    />,
    <Route
        key="/profiles"
        path="/profiles"
        exact={true}
        render={() => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            title: t("profiles_heading"),
                            type: 'website',
                            description: "profiles_description",
                            image: ROOT_URL + "/images/default/saints.webp",
                            keywords: t("profiles page keywords"),
                            url: ROOT_URL + window.location.pathname,
                        })
                    }
                </Translate>
            )
        }}
    />,

    <Route
        key="/musics"
        path="/musics"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: 'website',
                            url: ROOT_URL + staticPaths.musics.pathname,
                            image: ROOT_URL + "/images/default/catholic-hymns.webp",
                            keywords: t("musics page keywords"),
                            title: t('musics_heading'),
                            description: t("musics_description"),
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/song/:id"
        path="/song/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={musicSchema}>
                    {(item) => {
                        return (
                            (item || null) && (
                                <MetaData
                                    type={"Song"}
                                    url={ROOT_URL + '/song/' + item.idname}
                                    {...baseModel.getMeta(item)}
                                    image={ROOT_URL + "/images/default/catholic-hymns.webp"}
                                />
                            )
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/organizations"
        path="/organizations"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: 'website',
                            url: ROOT_URL + staticPaths.organizations.pathname,
                            image: ROOT_URL + "/images/default/catholic-organizations.webp",
                            title: t('organizations_heading'),
                            description: t("organizations_description"),
                            keywords: t("organizations page keywords")
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/products"
        path="/products"
        render={() => {
            return (
                <Translate>
                    {t =>
                        renderMeta({
                            type: 'website',
                            url: ROOT_URL + staticPaths.products.pathname,
                            title: t('products_heading'),
                            description: t(
                                'products_description'
                            ),
                            keywords: t(
                                'Catholic community support, faith-based initiatives, community services, Catholic life, nonprofit activities, resource sharing'
                            )
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/dioceses"
        path="/dioceses"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: 'website',
                            url: ROOT_URL + staticPaths.dioceses.pathname,
                            image: ROOT_URL + "/images/default/diocese-parish-map.webp",
                            title: t('dioceses_heading'),
                            description: t("dioceses_description"),
                            keywords: t("dioceses page keywords")
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/religious-orders"
        path="/religious-orders"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: 'website',
                            url: ROOT_URL + staticPaths.religiousOrder.pathname,
                            image: ROOT_URL + "/images/default/religious-orders.webp",
                            title: t('religious_orders_heading'),
                            description: t("religious_orders_description"),
                            keywords: t("religious orders keywords")
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/yellow-pages"
        path="/yellow-pages"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: 'website',
                            url: ROOT_URL + staticPaths.yellowPages.pathname,
                            title: t('yellow_pages_heading'),
                            description: t("yellow_pages_description"),
                            keywords: t("yellow_pages_keywords")
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/ecclesiastics"
        path="/ecclesiastics"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: 'website',
                            url: ROOT_URL + staticPaths.ecclesiasticDocument.pathname,
                            image: ROOT_URL + "/images/default/ecclesiastics.webp",
                            title: t('ecclesiastics_heading'),
                            description: t("ecclesiastics_description"),
                            keywords: t("Catholic Church documents, Canon law, Catholic teachings, Vatican documents, Catholic catechism, Apostolic letters, Encyclicals, Ecclesiastical publications, Church communications, Magisterium documents")
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/definitions"
        path="/definitions"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: 'website',
                            url: ROOT_URL + staticPaths.ecclesiasticDocument.pathname,
                            image: ROOT_URL + "/images/default/definitions.webp",
                            title: t('definitions_heading'),
                            description: t("definitions_description"),
                            keywords: t("Catholic glossary, Catholic definitions, theological terms, ecclesiastical vocabulary, Catholic dictionary, Christian terminology, religious glossary, Catholic faith terms, Catholic Church vocabulary, Catholic theological definitions")
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/definition/:id"
        path="/definition/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={definitionSchema}>
                    {(item) => {
                        return (
                            (item || null) && (
                                <MetaData
                                    description={item?.definition_details[0]}
                                    url={ROOT_URL + '/definition/' + item.id}
                                    image={ROOT_URL + "/images/default/definitions.webp"}
                                    {...baseModel.getMeta(item)}
                                />
                            )
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/dictionary/:id"
        path="/dictionary/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={dictionarySchema}>
                    {(item) => {
                        return (
                            (item || null) && (
                                <MetaData
                                    url={ROOT_URL + '/dictionary/' + item.idname}
                                    image={ROOT_URL + "/images/default/definitions.webp"}
                                    {...baseModel.getMeta(item)}
                                />
                            )
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/journey-to-christ"
        path="/journey-to-christ"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: 'website',
                            url: ROOT_URL + staticPaths.turnToChrist.pathname,
                            image: ROOT_URL + "/images/default/turn-to-christ.webp",
                            title: t('Journey to Christ – Faith, Hope, and Salvation'),
                            description: t("Journey to Christ and find faith, hope, and salvation. Return to Jesus, embrace God’s love, and experience a new life filled with grace and peace."),
                            keywords: t("Journey to Christ, Christian faith, return to Jesus, hope in Christ, salvation in Jesus, God’s love, new life in Christ, Christian spirituality")
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/spirituality"
        path="/spirituality"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: 'website',
                            url: ROOT_URL + staticPaths.spirituality.pathname,
                            image: ROOT_URL + staticPaths.spirituality.pathname,
                            title: t('spirituality_title'),
                            description: t('spirituality_description'),
                            keywords: t('spirituality_keywords')
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/spirituality?rel=journey"
        path="/spirituality?rel=journey"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: 'website',
                            url: ROOT_URL + staticPaths.spirituality.pathname,
                            image: ROOT_URL + staticPaths.spirituality.pathname,
                            title: t('spirituality_title'),
                            description: t('spirituality_description'),
                            keywords: t('spirituality_keywords')
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/evangelization"
        path="/evangelization"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: 'website',
                            url: ROOT_URL + staticPaths.evangelization.pathname,
                            image: ROOT_URL + "/images/banner_evangelization_page.jpg",
                            title: t('Evangelization is the mission of the whole Church and of every Christian'),
                            description: t("The message of the Lord cannot be confined. We are called to be living witnesses — salt, leaven, and bearers of the Light of the Gospel to all people"),
                            keywords: t("Journey to Christ, Christian faith, return to Jesus, hope in Christ, salvation in Jesus, God’s love, new life in Christ, Christian spirituality")
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/asks"
        path="/asks"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: 'website',
                            url: ROOT_URL + staticPaths.asks.pathname,
                            image: ROOT_URL + "/images/default/bible-asks.webp",
                            title: t('asks_heading'),
                            description: t("asks_description"),
                            keywords: t('Catholic questions, faith Q&A, spiritual questions, Catholic beliefs, theology answers, ask about God, Church teachings explained, Christian spirituality, explore Catholicism, religious reflection'),
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/courses"
        path="/courses"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: 'website',
                            url: ROOT_URL + staticPaths.courses.pathname,
                            image: ROOT_URL + "/images/default/courses.webp",
                            title: t('courses_heading'),
                            description: t("courses_description"),
                            keywords: t('Catholic courses, religious education, catechism classes, Bible study programs, theology courses, Catholic faith formation, online Catholic learning, Christian education, Catholic school programs, religious training'),
                        })
                    }
                </Translate>
            )
        }}
    />,
    <Route
        key="/event/:id"
        path="/event/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={eventSchema}>
                    {(item) => {

                        const schemaOrg = _.get(item, 'schema')
                        const structuredJSON = schemaOrg || undefined

                        return (
                            (item &&
                                renderMeta({
                                    url: ROOT_URL + '/event/' + item.idname,
                                    title: _.get(item, 'overview.title'),
                                    description: _.get(item, 'overview.description'),
                                    keywords: _.get(item, 'overview.keywords'),
                                    photo: _.get(item, 'overview.photo'),
                                    structuredJSON,
                                })) ||
                            null
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/classroom/:id"
        path="/classroom/:id"
        render={(props) => {
            const id = _.get(props, 'match.params.id')
            return (
                <SelectEntityItem
                    item={id}
                    schema={courseClassSchema}>
                    {(item) => {
                        return (
                            (item || null) && (
                                <MetaData
                                    url={ROOT_URL + '/classroom/' + item.id}
                                    {...baseModel.getMeta(item)}
                                />
                            )
                        )
                    }}
                </SelectEntityItem>
            )
        }}
    />,
    <Route
        key="/calendar"
        path="/calendar"
        render={(props) => {
            return (
                <Translate>
                    {
                        t => renderMeta({
                            type: 'website',
                            url: ROOT_URL + staticPaths.calendar.pathname,
                            title: t('liturgy calendar'),
                            description: t("Follow the complete liturgical seasons, solemnities, and celebrations of the Church to fully live your faith"),
                        })
                    }
                </Translate>
            )
        }}
    />,

    <Route
        key="/"
        path="/"
        exact={true}
        render={() => {
            const host = window.location.host
            const hostArray = host.split('.')
            if (!host.includes('localhost')) {
                hostArray.pop()
            }
            hostArray.pop()

            if (!!_.first(hostArray)) {
                return (
                    <SelectEntityItem
                        item={_.first(hostArray)}
                        schema={organizationSchema}>
                        {(item) => {
                            return (
                                (item || null) && (
                                    <MetaData
                                        url={window.location.host}
                                        {...baseModel.getMeta(item)}
                                    />
                                )
                            )
                        }}
                    </SelectEntityItem>
                )
            }

            return renderMeta({
                title: aliasTitle['home'],
                type: 'website',
                description: aliasDescription['home'],
                keywords: aliasKeywords['home'],
                url: ROOT_URL + window.location.pathname,
            })
        }}
    />,
    <Route
        key="home"
        path=""
        exact={true}
        render={() => {
            const host = window.location.host
            const hostArray = host.split('.')
            if (!host.includes('localhost')) {
                hostArray.pop()
            }
            hostArray.pop()

            if (!!_.first(hostArray)) {
                return (
                    <SelectEntityItem
                        item={_.first(hostArray)}
                        schema={organizationSchema}>
                        {(item) => {
                            return (
                                (item || null) && (
                                    <MetaData
                                        url={window.location.host}
                                        {...baseModel.getMeta(item)}
                                    />
                                )
                            )
                        }}
                    </SelectEntityItem>
                )
            }

            return renderMeta({
                title: aliasTitle['home'],
                type: 'website',
                description: aliasDescription['home'],
                keywords: aliasKeywords['home'],
                url: ROOT_URL + window.location.pathname,
            })
        }}
    />,
    <Route
        key="/:type/:id"
        path="/:type/:id"
        exact={true}
        render={(props) => {
            const type = _.upperFirst(_.get(props, 'match.params.type'))
            return renderMeta({
                title: type,
                url: ROOT_URL + window.location.pathname,
            })
        }}
    />,
    <Route
        key="/:type"
        path="/:type"
        exact={true}
        render={(props) => {
            const type = _.upperFirst(
                aliasTitle[_.get(props, 'match.params.type')] ??
                _.get(props, 'match.params.type')
            )
            return renderMeta({
                title: type,
                url: ROOT_URL + window.location.pathname,
            })
        }}
    />,

    <Route
        key="*"
        path="*"
        render={() =>
            renderMeta({
                title: _.upperFirst(
                    _.get(window.location.pathname.split('/'), '1', 'Kristo Network')
                ),
                url: ROOT_URL + window.location.pathname,
            })
        }
    />,
]
const LoadMeta = ({config}) => {
    const {location = {}} = useRouter()

    return <Switch location={location}>{routes}</Switch>
}
export default LoadMeta
